/**
 * LOR Metadata Transformation Utilities
 * 
 * This module provides utilities for transforming Light-O-Rama (LOR) metadata
 * from Supabase format to Typesense format, ensuring proper handling of
 * arrays, ranges, optional fields, and data validation.
 */

export interface LORMetadataSupabase {
  id?: string;
  controller_type?: 'AC_ONLY' | 'RGB' | 'MIXED' | null;
  channel_count_ac?: number | null;
  software_version?: 'S4' | 'S5' | 'S6' | null;
  prop_types?: string[] | null;
  license_level_required?: 'BASIC' | 'STANDARD' | 'ADVANCED' | 'PRO' | null;
  sequence_type_tags?: ('TRADITIONAL' | 'RGB_PLUS' | 'SINGING_FACES')[] | null;
  import_notes?: string | null;
}

export interface LORMetadataTypesense {
  lor_controller_type: string | null;
  lor_channel_count_ac: number | null;
  lor_software_version: string | null;
  lor_license_level_required: string | null;
  lor_prop_types: string[];
  lor_sequence_type_tags: string[];
  lor_import_notes: string | null;
  lor_compatible: boolean;
  lor_has_channel_count: boolean;
  lor_has_prop_types: boolean;
}

/**
 * Validate channel count range (1-512)
 */
export function validateChannelCount(count: number | null | undefined): number | null {
  if (count === null || count === undefined) {
    return null;
  }
  
  const numCount = Number(count);
  if (isNaN(numCount) || numCount < 1 || numCount > 512) {
    console.warn(`Invalid channel count: ${count}. Must be between 1-512.`);
    return null;
  }
  
  return numCount;
}

/**
 * Validate and normalize controller type
 */
export function validateControllerType(type: string | null | undefined): string | null {
  if (!type) {
    return null;
  }
  
  const validTypes = ['AC_ONLY', 'RGB', 'MIXED'];
  if (!validTypes.includes(type)) {
    console.warn(`Invalid controller type: ${type}. Must be one of: ${validTypes.join(', ')}`);
    return null;
  }
  
  return type;
}

/**
 * Validate and normalize software version
 */
export function validateSoftwareVersion(version: string | null | undefined): string | null {
  if (!version) {
    return null;
  }
  
  const validVersions = ['S4', 'S5', 'S6'];
  if (!validVersions.includes(version)) {
    console.warn(`Invalid software version: ${version}. Must be one of: ${validVersions.join(', ')}`);
    return null;
  }
  
  return version;
}

/**
 * Validate and normalize license level
 */
export function validateLicenseLevel(level: string | null | undefined): string | null {
  if (!level) {
    return null;
  }
  
  const validLevels = ['BASIC', 'STANDARD', 'ADVANCED', 'PRO'];
  if (!validLevels.includes(level)) {
    console.warn(`Invalid license level: ${level}. Must be one of: ${validLevels.join(', ')}`);
    return null;
  }
  
  return level;
}

/**
 * Validate and normalize prop types array
 */
export function validatePropTypes(propTypes: string[] | null | undefined): string[] {
  if (!propTypes || !Array.isArray(propTypes)) {
    return [];
  }
  
  // Filter out empty strings and normalize
  return propTypes
    .filter(type => type && typeof type === 'string' && type.trim() !== '')
    .map(type => type.trim())
    .slice(0, 20); // Limit to 20 prop types as per schema
}

/**
 * Validate and normalize sequence type tags array
 */
export function validateSequenceTypeTags(tags: string[] | null | undefined): string[] {
  if (!tags || !Array.isArray(tags)) {
    return [];
  }
  
  const validTags = ['TRADITIONAL', 'RGB_PLUS', 'SINGING_FACES'];
  
  return tags
    .filter(tag => tag && typeof tag === 'string' && validTags.includes(tag))
    .slice(0, 10); // Limit to 10 tags as per schema
}

/**
 * Validate and normalize import notes
 */
export function validateImportNotes(notes: string | null | undefined): string | null {
  if (!notes || typeof notes !== 'string') {
    return null;
  }
  
  const trimmedNotes = notes.trim();
  if (trimmedNotes.length === 0) {
    return null;
  }
  
  // Limit to 2000 characters as per schema
  if (trimmedNotes.length > 2000) {
    console.warn(`Import notes truncated from ${trimmedNotes.length} to 2000 characters`);
    return trimmedNotes.substring(0, 2000);
  }
  
  return trimmedNotes;
}

/**
 * Transform LOR metadata from Supabase format to Typesense format
 */
export function transformLORMetadata(lorMeta: LORMetadataSupabase | null | undefined): LORMetadataTypesense {
  // Handle null/undefined input
  if (!lorMeta) {
    return {
      lor_controller_type: null,
      lor_channel_count_ac: null,
      lor_software_version: null,
      lor_license_level_required: null,
      lor_prop_types: [],
      lor_sequence_type_tags: [],
      lor_import_notes: null,
      lor_compatible: false,
      lor_has_channel_count: false,
      lor_has_prop_types: false
    };
  }

  // Validate and transform individual fields
  const controllerType = validateControllerType(lorMeta.controller_type);
  const channelCount = validateChannelCount(lorMeta.channel_count_ac);
  const softwareVersion = validateSoftwareVersion(lorMeta.software_version);
  const licenseLevel = validateLicenseLevel(lorMeta.license_level_required);
  const propTypes = validatePropTypes(lorMeta.prop_types);
  const sequenceTypeTags = validateSequenceTypeTags(lorMeta.sequence_type_tags);
  const importNotes = validateImportNotes(lorMeta.import_notes);

  // Determine if this is a LOR-compatible sequence
  const isLORCompatible = !!(controllerType || softwareVersion || licenseLevel);
  
  // Determine if channel count is specified
  const hasChannelCount = !!(channelCount);
  
  // Determine if prop types are specified
  const hasPropTypes = !!(propTypes && propTypes.length > 0);

  return {
    lor_controller_type: controllerType,
    lor_channel_count_ac: channelCount,
    lor_software_version: softwareVersion,
    lor_license_level_required: licenseLevel,
    lor_prop_types: propTypes,
    lor_sequence_type_tags: sequenceTypeTags,
    lor_import_notes: importNotes,
    lor_compatible: isLORCompatible,
    lor_has_channel_count: hasChannelCount,
    lor_has_prop_types: hasPropTypes
  };
}

/**
 * Extract LOR metadata from a sequence object
 */
export function extractLORMetadata(sequence: any): LORMetadataSupabase | null {
  if (!sequence) {
    return null;
  }

  // Check if sequence has LOR metadata
  const lorMeta = sequence.sequence_lor_meta;
  if (!lorMeta) {
    return null;
  }

  return {
    id: lorMeta.id,
    controller_type: lorMeta.controller_type,
    channel_count_ac: lorMeta.channel_count_ac,
    software_version: lorMeta.software_version,
    prop_types: lorMeta.prop_types,
    license_level_required: lorMeta.license_level_required,
    sequence_type_tags: lorMeta.sequence_type_tags,
    import_notes: lorMeta.import_notes
  };
}

/**
 * Generate search text from LOR metadata
 */
export function generateLORSearchText(lorMeta: LORMetadataTypesense): string {
  const searchTerms: string[] = [];

  if (lorMeta.lor_controller_type) {
    searchTerms.push(lorMeta.lor_controller_type);
  }

  if (lorMeta.lor_software_version) {
    searchTerms.push(lorMeta.lor_software_version);
  }

  if (lorMeta.lor_license_level_required) {
    searchTerms.push(lorMeta.lor_license_level_required);
  }

  if (lorMeta.lor_prop_types.length > 0) {
    searchTerms.push(...lorMeta.lor_prop_types);
  }

  if (lorMeta.lor_sequence_type_tags.length > 0) {
    searchTerms.push(...lorMeta.lor_sequence_type_tags);
  }

  if (lorMeta.lor_import_notes) {
    searchTerms.push(lorMeta.lor_import_notes);
  }

  return searchTerms.join(' ').toLowerCase();
}

/**
 * Validate LOR metadata for specific controller type constraints
 */
export function validateLORConstraints(lorMeta: LORMetadataTypesense): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check if channel count is required for AC_ONLY and MIXED controllers
  if (lorMeta.lor_controller_type === 'AC_ONLY' || lorMeta.lor_controller_type === 'MIXED') {
    if (!lorMeta.lor_has_channel_count) {
      errors.push(`Channel count is recommended for ${lorMeta.lor_controller_type} controllers`);
    }
  }

  // Check if channel count is provided for RGB controller (should not be)
  if (lorMeta.lor_controller_type === 'RGB' && lorMeta.lor_has_channel_count) {
    errors.push('Channel count should not be specified for RGB controllers');
  }

  // Check if prop types are provided for RGB controller (recommended)
  if (lorMeta.lor_controller_type === 'RGB' && !lorMeta.lor_has_prop_types) {
    errors.push('Prop types are recommended for RGB controllers');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Get LOR metadata summary for logging/debugging
 */
export function getLORMetadataSummary(lorMeta: LORMetadataTypesense): string {
  const parts: string[] = [];

  if (lorMeta.lor_controller_type) {
    parts.push(`Controller: ${lorMeta.lor_controller_type}`);
  }

  if (lorMeta.lor_software_version) {
    parts.push(`Software: ${lorMeta.lor_software_version}`);
  }

  if (lorMeta.lor_license_level_required) {
    parts.push(`License: ${lorMeta.lor_license_level_required}`);
  }

  if (lorMeta.lor_has_channel_count) {
    parts.push(`Channels: ${lorMeta.lor_channel_count_ac}`);
  }

  if (lorMeta.lor_has_prop_types) {
    parts.push(`Props: ${lorMeta.lor_prop_types.length} types`);
  }

  if (lorMeta.lor_sequence_type_tags.length > 0) {
    parts.push(`Tags: ${lorMeta.lor_sequence_type_tags.join(', ')}`);
  }

  return parts.length > 0 ? parts.join(' | ') : 'No LOR metadata';
}
